import {useEffect, useRef, useCallback} from 'react';
import {FontManager} from '@contentlab/services/fonts';
import fontsStore from '@contentlab/stores/fontStore';
import {FontVariants} from '@contentlab/types/captions';
import {FALLBACK_FONT_FAMILIES} from '@contentlab/views/contentlab/pages/captions-page/components/style-editor/SharedConstants';

/**
 * Hook to initialize fonts once across the entire app
 * Uses React mechanisms to prevent duplicate initialization
 */
export default function useFontManager(): void {
  const fontsMetadata = fontsStore((state) => state.fontsMetadata);
  const initializationAttempted = useRef(false);

  const initializeFonts = useCallback(async () => {
    // Guard against React.StrictMode double execution and multiple calls
    if (initializationAttempted.current || fontsMetadata.size > 0) {
      return;
    }

    initializationAttempted.current = true;

    try {
      await FontManager.initialize();

      // Preload fallback fonts
      const fallbackFontPromises = FALLBACK_FONT_FAMILIES.flatMap((font) =>
        [FontVariants.REGULAR, FontVariants.BOLD, FontVariants.ITALIC].map(
          (variant) => FontManager.loadFont(font.id, variant)
        )
      );
      await Promise.all(fallbackFontPromises);
    } catch (error) {
      // Reset flag on error so we can retry
      initializationAttempted.current = false;
      console.error('Failed to initialize fonts:', error);
    }
  }, [fontsMetadata.size]);

  useEffect(() => {
    initializeFonts();
  }, [initializeFonts]);
}
